/**
 * The real (browser-side) `RigMixerFactory` for the rig animator (AP5.S1),
 * wrapping `THREE.AnimationMixer` with the standard fade-to-action blend used by
 * three's own skinning-blending example: fade the outgoing action out, reset the
 * incoming one and fade it in, so locomotion transitions never pop.
 *
 * AP5.S2 adds `playOnce`: a `LoopOnce` action for the one-shot clips (slash, hit
 * reaction, death, collect). Every one-shot sets `clampWhenFinished` — even the
 * ones the animator hands straight back to locomotion — because a finished,
 * un-clamped action is DISABLED by three, and `PropertyMixer.apply` blends any
 * weight shortfall toward the bind pose: cross-fading locomotion back in over a
 * disabled one-shot would dip the whole rig toward T-pose for the fade duration.
 * Holding the last frame keeps the total weight at 1 through the return fade.
 * The `clamp` option instead selects WHEN completion is reported: a clamped clip
 * (death) reports on three's `finished` event and holds; an un-clamped clip
 * reports `fadeSeconds` early so the animator's return cross-fade lands exactly on
 * the clip's end — no visible hold, no dip. A `maxSeconds` hold budget (the
 * animator's per-action `holdSeconds`, AP5.S2 fix 2026-09-02) moves that end
 * point earlier for a clip longer than its action deserves (`Left_Slash` is
 * 3.17 s; its strokes are over by 1.5 s), and the same early rule applies to it,
 * so the return fade lands on the budget. Completion is dispatched from
 * `update()` AFTER `mixer.update` returns, never from inside three's action loop
 * (the `finished` listener only flags it), so the animator's response — a
 * `play()` that activates another action — never mutates the action list three is
 * iterating.
 *
 * Injected into `RigAnimator` exactly as `createThreeGltfLoader` is into
 * `AssetLoader` — it is deliberately NOT imported by the animator core or its
 * tests, which stay `three`-free and node-runnable (AD-120). Mirrors
 * assets/threeLoader.ts in role and shape.
 *
 * `uncacheRoot` on stop is what actually frees the mixer's per-root action cache;
 * without it a re-attached avatar (rig rebuild on a content-pack re-trigger) would
 * accumulate stale actions against a disposed graph.
 *
 * `addClips` (q-rig-pack-clips-without-reattach) registers late clip packs on the LIVE
 * mixer. The low tier's packs used to re-attach the rig, which built a new mixer and
 * restarted the running stride from frame 0. Three binds an action on the first
 * `clipAction`, so registering a clip is just making it findable by name.
 */

import {
  AnimationClip,
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  type AnimationAction,
  type Object3D,
} from 'three';

import type {
  LoopPlayOptions,
  PlayOnceOptions,
  RigClip,
  RigMixerFactory,
  RigMixerHandle,
} from './animator.js';

/**
 * Processed (in-place) clip sets, memoised per source array so the conversion
 * below runs ONCE for the shared cached clips rather than per avatar.
 */
const inPlaceCache = new WeakMap<object, AnimationClip[]>();

/** The name of the topmost bone in a rig — the one carrying any root motion. */
function findRootBoneName(root: Object3D): string | null {
  let name: string | null = null;
  root.traverse((node) => {
    if (name !== null) return;
    const isBone = (node as unknown as { isBone?: unknown }).isBone === true;
    const parentIsBone = (node.parent as unknown as { isBone?: unknown } | null)?.isBone === true;
    if (isBone && !parentIsBone) name = node.name;
  });
  return name;
}

/**
 * How much of the root bone's horizontal range must survive as NET displacement
 * (last frame minus first) before the clip counts as TRAVELLING.
 *
 * Travel is "the root ends up somewhere else", not "the root moves a lot": a walk
 * carries the root a stride and never brings it back, while an idle sway or an
 * attack lunge goes out and returns. The previous test (horizontal range > 8 ×
 * vertical range) could not tell a lunge from a walk, so it pinned every flat
 * lunge, charge and pounce (owner, 2026-10-19). Measured over all 477 shipped
 * clips (326 GLBs) the net test pins the same 7 as the old ratio did — the
 * player's turn/sprint locomotion, net 12–323 cm — and nothing else.
 */
const NET_TRAVEL_FRACTION = 0.5;

/** Min/max of one component (stride-3) of a position track's values. */
function componentRange(values: ArrayLike<number>, offset: number): number {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (let i = offset; i + 2 < values.length + offset; i += 3) {
    const v = values[i];
    if (v === undefined) break;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return max > min ? max - min : 0;
}

/**
 * Convert only genuinely TRAVELLING clips to in-place playback (AP5.S1).
 *
 * The server owns world position (movement is simulated in `shared/sim`, AD-026),
 * so a clip that ALSO walks the character forward would apply position twice and
 * the avatar would slide off its own collider and health bar. Such a clip has its
 * root bone's horizontal (x/z) translation pinned to the first frame.
 *
 * It is deliberately CONDITIONAL. Root movement that returns to where it started
 * (a run's weight shift, an idle sway, an attack's lunge-and-recover) never moves
 * the character, so it is kept; only a clip whose root ENDS displaced (see
 * `NET_TRAVEL_FRACTION`) is pinned — the guard that keeps a rig authored with
 * root motion from sliding the moment it lands, with no code change.
 *
 * Vertical (y) is always kept — that is the bob. Rotation tracks are untouched.
 * The source clips are shared immutable data (`AssetLoader.clipCache`), so this
 * clones before editing; mutating them would corrupt every other avatar.
 */
function toInPlace(clips: readonly AnimationClip[], rootBoneName: string | null): AnimationClip[] {
  if (rootBoneName === null) return clips as AnimationClip[];
  const cached = inPlaceCache.get(clips as unknown as object);
  if (cached !== undefined) return cached;

  const positionTrack = `${rootBoneName}.position`;
  const converted = clips.map((clip) => {
    const track = clip.tracks.find((t) => t.name === positionTrack);
    if (track === undefined) return clip; // no root position track — share the original
    const values = track.values;
    const last = values.length - 3;
    const horizontal = Math.max(componentRange(values, 0), componentRange(values, 2));
    const vertical = componentRange(values, 1);
    const net = Math.hypot(
      (values[last] ?? 0) - (values[0] ?? 0),
      (values[last + 2] ?? 0) - (values[2] ?? 0),
    );
    // Travel = the root ends up elsewhere: most of its span is net displacement, and
    // more than its bob (so a sub-bob drift is still sway).
    const travels = horizontal > 0 && net > horizontal * NET_TRAVEL_FRACTION && net > vertical;
    if (!travels) return clip; // returns to its start — keep the sway / lunge

    const copy = clip.clone();
    for (const t of copy.tracks) {
      if (t.name !== positionTrack) continue;
      const values = t.values;
      const firstX = values[0] ?? 0;
      const firstZ = values[2] ?? 0;
      for (let i = 0; i + 2 < values.length; i += 3) {
        values[i] = firstX; // x pinned — no horizontal travel
        values[i + 2] = firstZ; // z pinned
        // values[i + 1] (y) untouched — keep the vertical bob.
      }
    }
    return copy;
  });
  inPlaceCache.set(clips as unknown as object, converted);
  return converted;
}

/** The one-shot in flight on a mixer (AP5.S2), until its completion is reported. */
interface OneShot {
  action: AnimationAction;
  /**
   * CLIP seconds the one-shot owns the pose: the clip length, or a shorter
   * `maxSeconds` budget. Compared against `action.time`, which three advances at
   * the action's own time scale — so this is clip time, never wall-clock.
   */
  holdSeconds: number;
  fadeSeconds: number;
  /** Playback rate in force, ≥ 0 and finite. 1 unless the caller asked otherwise. */
  timeScale: number;
  clamp: boolean;
  onFinished: (() => void) | undefined;
  /** Set by three's `finished` listener; dispatched from `update()` (re-entrancy guard). */
  finished: boolean;
}

/**
 * Build a `RigMixerFactory` backed by `THREE.AnimationMixer`. One factory serves
 * every avatar; each `attach` constructs its own mixer bound to that avatar's
 * root (mixers are per-root by design).
 */
export function createThreeRigMixer(): RigMixerFactory {
  return (root: Object3D, clips: readonly RigClip[]): RigMixerHandle | null => {
    if (clips.length === 0) return null;
    // The animator passes the clips straight through from the loader, so they are
    // real AnimationClips; the structural `RigClip` type is only the node-safe view.
    const sourceClips = clips as unknown as AnimationClip[];
    const rootBoneName = findRootBoneName(root);
    // Strip root-motion travel so the network stays the single source of position.
    // `addClips` REASSIGNS this, never pushes into it: `toInPlace` returns either the caller's
    // own array or a cached one shared by every avatar on the same clip set.
    let realClips: AnimationClip[] = toInPlace(sourceClips, rootBoneName);
    const mixer = new AnimationMixer(root);
    let active: AnimationAction | null = null;
    /** The last LOOPING action `play()` started — the one `setLoopRate` drives. */
    let loop: AnimationAction | null = null;
    let oneShot: OneShot | null = null;

    // Flag only — the completion is reported from update(), after three's loop.
    mixer.addEventListener('finished', (event) => {
      if (oneShot !== null && event.action === oneShot.action) oneShot.finished = true;
    });

    /** Report the in-flight one-shot's completion exactly once, then forget it. */
    const settle = (): void => {
      if (oneShot === null) return;
      const callback = oneShot.onFinished;
      oneShot = null; // before the callback: its `play()` must not see a stale one-shot
      callback?.();
    };

    return {
      update(dtSeconds: number): void {
        mixer.update(dtSeconds);
        if (oneShot === null) return;
        // Un-clamped: one fade early, so the return cross-fade lands on the hold
        // end. Clamped: at the hold end itself (three's `finished` flag covers the
        // clip-end case; the time test covers a budget shorter than the clip).
        // ⚠ `holdSeconds` and `action.time` are CLIP seconds; a fade is WALL-CLOCK.
        // The lead is therefore `fadeSeconds * timeScale` clip seconds — at rate 1
        // that is the original expression, and at any other rate the un-corrected
        // form would start the return fade late (fast) or early (slow).
        const threshold = oneShot.clamp
          ? oneShot.holdSeconds
          : oneShot.holdSeconds - oneShot.fadeSeconds * oneShot.timeScale;
        if (oneShot.finished || oneShot.action.time >= threshold) settle();
      },

      play(clipName: string, fadeSeconds: number, options: LoopPlayOptions = {}): void {
        const clip = AnimationClip.findByName(realClips, clipName);
        if (!clip) return; // resolver named a clip this rig does not carry ⇒ keep current
        const next = mixer.clipAction(clip);
        if (next === active) return;
        // q-walk-turn-restarts-stride (owner, Desk #849): a loop re-entered while it is
        // still fading OUT (a one-frame idle as the stick crosses zero on a turn) carries on
        // from where it is. Restarting it from frame 0 made every turn read as a stall.
        const resumeAt = next.isRunning() ? next.time : null;
        // A walk↔run swap starts the new loop at the outgoing loop's point in its cycle.
        let phaseAt: number | null = null;
        if (resumeAt === null && options.syncPhase === true && loop !== null && loop.isRunning()) {
          const outgoing = loop.getClip().duration;
          if (outgoing > 0) phaseAt = ((loop.time % outgoing) / outgoing) * clip.duration;
        }
        // Locomotion loops; undo any one-shot settings a shared clip may carry —
        // including a `timeScale`, which three stores on the ACTION and would
        // otherwise leak into locomotion on a rig that names one clip for both.
        next.setLoop(LoopRepeat, Number.POSITIVE_INFINITY);
        next.clampWhenFinished = false;
        if (active !== null) active.fadeOut(fadeSeconds);
        next.reset().setEffectiveTimeScale(1).setEffectiveWeight(1);
        if (resumeAt !== null) {
          // Full weight at once, no fade in: a fade in starts at 0, and with the outgoing
          // clip already part-faded the weights would sum below 1 and blend toward bind pose.
          next.time = resumeAt;
          next.play();
        } else {
          if (phaseAt !== null) next.time = phaseAt;
          next.fadeIn(fadeSeconds).play();
        }
        active = next;
        loop = next;
        // A displaced one-shot's completion is moot — the caller chose a new clip.
        if (oneShot !== null && oneShot.action !== next) oneShot = null;
      },

      setLoopRate(rate: number): void {
        // Speed-matched locomotion: only the looping clip, and only a usable rate (the same
        // finite-and-positive rule as a one-shot's). A one-shot layered over the loop keeps
        // its own rate — its action is a different AnimationAction.
        if (loop === null || !(Number.isFinite(rate) && rate > 0)) return;
        if (oneShot !== null && oneShot.action === loop) return; // a shared clip playing once
        loop.setEffectiveTimeScale(rate);
      },

      playOnce(clipName: string, fadeSeconds: number, options: PlayOnceOptions = {}): void {
        const clip = AnimationClip.findByName(realClips, clipName);
        if (!clip) return; // rig lacks the clip ⇒ the current pose keeps playing
        const next = mixer.clipAction(clip);
        next.setLoop(LoopOnce, 1);
        next.clampWhenFinished = true; // always — see the header on the bind-pose dip
        // A caller's rate is honoured only when it is a finite, strictly positive
        // number: 0 would freeze the pose forever (the budget is clip time, so it
        // could never elapse) and a negative rate would run the clip backwards
        // past its start. Neither is a thing an action policy should be able to
        // say by accident, so both fall back to the clip's own rate.
        const rate =
          typeof options.timeScale === 'number' &&
          Number.isFinite(options.timeScale) &&
          options.timeScale > 0
            ? options.timeScale
            : 1;
        if (next === active) {
          // Restart in place at full weight: a fresh fade-in here would dip toward
          // bind pose, since nothing else is carrying weight.
          next.reset().setEffectiveTimeScale(rate).setEffectiveWeight(1).play();
        } else {
          if (active !== null) active.fadeOut(fadeSeconds);
          next.reset().setEffectiveTimeScale(rate).setEffectiveWeight(1).fadeIn(fadeSeconds).play();
          active = next;
        }
        oneShot = {
          action: next,
          holdSeconds: Math.min(clip.duration, options.maxSeconds ?? Number.POSITIVE_INFINITY),
          fadeSeconds,
          timeScale: rate,
          clamp: options.clamp === true,
          onFinished: options.onFinished,
          finished: false,
        };
      },

      addClips(extra: readonly RigClip[]): void {
        // Late clip packs join this mixer: the actions already playing are untouched (no
        // reset, no fade), so a running stride keeps its phase. Same in-place rule as the
        // first set, so a travelling pack clip never adds root motion.
        const have = new Set(realClips.map((clip) => clip.name));
        const fresh = toInPlace(extra as unknown as AnimationClip[], rootBoneName).filter(
          (clip) => !have.has(clip.name),
        );
        if (fresh.length > 0) realClips = [...realClips, ...fresh];
      },

      stop(): void {
        mixer.stopAllAction();
        // Free the per-root action cache — see the header note on rig rebuilds.
        mixer.uncacheRoot(root);
        active = null;
        loop = null;
        oneShot = null;
      },
    };
  };
}
